import React, {useCallback, useMemo, useState} from 'react';
import {PlayerSession} from '../../interfaces';
import {getTime} from '../../services/dateService';
import ProfileIdentity from '../shared/ProfileIdentity';
import {getSpeedMultiplier} from '../../services/measurementService';
import {useNavigate} from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import {Positions, SessionTypes} from '../../enums';
import PageSubtitle from '../shared/PageSubtitle';
import {useTranslation} from 'react-i18next';
import {TFunction} from 'i18next';
import {deletePlayerSession} from '../../api/playerSessionAPI';
import {getTeamSessionGroupDetailAsync} from '../../store/teamSessionGroupSlice';
import {useAppDispatch} from '../../store/hooks';
import {getTeamSessionDetailAsync} from '../../store/teamSessionSlice';

const CELL_HEIGHT = 65;
const OPTIONS_COLUMN_WIDTH = 50;

interface Column {
  field: keyof PlayerSession;
  headerName: string;
  valueFormatter?: (value?: number) => string | number | null | undefined;
}

const getColumns = ({
  preferredUnits,
  isGame,
  t,
}: {
  preferredUnits?: 'imperial' | 'metric';
  isGame?: boolean;
  t: TFunction;
}): Column[] => {
  return [
    // Games report a cloud-sourced time on ice; other session types show active time.
    {
      field: isGame ? 'timeOnIce' : 'activeTime',
      headerName: isGame
        ? t('teamSessionPlayerList.timeOnIce')
        : t('teamSessionPlayerList.activeTime'),
      valueFormatter: value => {
        return getTime(value);
      },
    },
    {
      field: 'strintTotalTime',
      headerName: t('teamSessionPlayerList.strintTotalTime'),
      valueFormatter: value => {
        return getTime(value);
      },
    },
    {
      field: 'energyRatio',
      headerName: t('teamSessionPlayerList.energyRatio'),
      valueFormatter: value => {
        if (value) {
          return `${(value * 100).toFixed(1)}%`;
        }
        return value;
      },
    },
    {
      field: 'cumulative3DPlayerLoadV2',
      headerName: t('teamSessionPlayerList.playerLoad'),
      valueFormatter: value => {
        if (value) {
          return Number((value / 100).toFixed(0));
        }
        return value;
      },
    },
    {
      field: 'hustleScoreTopLevel',
      headerName: t('teamSessionPlayerList.hustleScore'),
      valueFormatter: value => {
        if (value) {
          return Math.round(value);
        }
        return value;
      },
    },
    {
      field: 'strideCountLeft',
      headerName: t('teamSessionPlayerList.strideCountLeft'),
    },
    {
      field: 'strideCountRight',
      headerName: t('teamSessionPlayerList.strideCountRight'),
    },
    {
      field: 'speedAvgV2',
      headerName: t('teamSessionPlayerList.speedAvg', {
        units: preferredUnits === 'metric' ? 'kph' : 'mph',
      }),
      valueFormatter: value => {
        if (value) {
          return Number(
            (value * getSpeedMultiplier(preferredUnits === 'metric')).toFixed(
              2,
            ),
          );
        }
      },
    },
    {
      field: 'speedMaxV2',
      headerName: t('teamSessionPlayerList.speedMax', {
        units: preferredUnits === 'metric' ? 'kph' : 'mph',
      }),
      valueFormatter: value => {
        if (value) {
          return Number(
            (value * getSpeedMultiplier(preferredUnits === 'metric')).toFixed(
              2,
            ),
          );
        }
      },
    },
    {
      field: 'accelAvgV2',
      headerName: t('teamSessionPlayerList.accelAvg'),
      valueFormatter: value => {
        if (value) {
          return Number((value / 9.8).toFixed(2));
        }
        return value;
      },
    },
    {
      field: 'accelMaxV2',
      headerName: t('teamSessionPlayerList.accelMax'),
      valueFormatter: value => {
        if (value) {
          return Number((value / 9.8).toFixed(2));
        }
        return value;
      },
    },
    {
      field: 'strintFilteredStrideRateAvgV2',
      headerName: t('teamSessionPlayerList.agilityAvg'),
      valueFormatter: value => {
        if (value) {
          return Number(value.toFixed(0));
        }
        return value;
      },
    },
    {
      field: 'strintFilteredStrideRateMaxV2',
      headerName: t('teamSessionPlayerList.agilityMax'),
      valueFormatter: value => {
        if (value) {
          return Number(value.toFixed(0));
        }
        return value;
      },
    },
    {
      field: 'balanceNormalized',
      headerName: t('teamSessionPlayerList.balanceNormalized'),
      valueFormatter: value => {
        if (value) {
          return `${((value / 0.5) * 100).toFixed(1)}%`;
        }
        return value;
      },
    },
    {
      field: 'calculatedShiftCount',
      headerName: t('teamSessionPlayerList.shiftCount'),
    },
    {
      field: 'shiftTimeAvg',
      headerName: t('teamSessionPlayerList.shiftTimeAvg'),
      valueFormatter: value => {
        return getTime(value);
      },
    },
    {
      field: 'hrAvg',
      headerName: t('teamSessionPlayerList.hrAvg'),
      valueFormatter: value => {
        if (value) {
          return Number(value.toFixed(0));
        }
        return value;
      },
    },
    {
      field: 'hrMax',
      headerName: t('teamSessionPlayerList.hrMax'),
      valueFormatter: value => {
        if (value) {
          return Number(value.toFixed(0));
        }
        return value;
      },
    },
  ];
};

interface Props {
  teamId: number;
  teamSessionId?: number;
  teamSessionGroupId?: number;
  preferredUnits?: 'imperial' | 'metric';
  sessionType?: string;
  data: PlayerSession[];
}

const TeamSessionPlayerList: React.FC<Props> = ({
  teamId,
  teamSessionId,
  teamSessionGroupId,
  preferredUnits,
  sessionType,
  data,
}) => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedPlayerSession, setSelectedPlayerSession] =
    useState<PlayerSession | null>(null);
  const [playerIdDeleting, setPlayerIdDeleting] = useState<number | null>(null);

  const handleNavigateToPlayerSessionDetail = useCallback(
    (playerSession: PlayerSession) => {
      // TODO FIXME, the route to player session group detail should be a different route
      // it should be something like /teams/${teamId}/teamsessiongroups/${teamSessionGroupId}/playersessions/${playerSession?.player?.profile?.id}
      navigate(
        // ? `/teams/${teamId}/teamsessiongroups/${teamSessionGroupId}/playersessions/${playerSession?.player?.profile?.id}`
        teamSessionGroupId
          ? `/teams/${teamId}/players/${playerSession?.player?.profile?.id}/playersessions/groups/${teamSessionGroupId}#tsg`
          : `/teams/${teamId}/teamsessions/${teamSessionId}/playersessions/${playerSession.id}`,
      );
    },
    [navigate, teamId, teamSessionGroupId, teamSessionId],
  );

  const handleMenuOpen = useCallback(
    (event: React.MouseEvent<HTMLElement>, playerSession: PlayerSession) => {
      event.stopPropagation();
      setMenuAnchorEl(event.currentTarget);
      setSelectedPlayerSession(playerSession);
    },
    [],
  );

  const handleMenuClose = useCallback(() => {
    setMenuAnchorEl(null);
    setSelectedPlayerSession(null);
  }, []);

  const handleView = useCallback(() => {
    if (selectedPlayerSession) {
      handleNavigateToPlayerSessionDetail(selectedPlayerSession);
    }
    handleMenuClose();
  }, [
    handleMenuClose,
    handleNavigateToPlayerSessionDetail,
    selectedPlayerSession,
  ]);

  const handleDelete = useCallback(async () => {
    handleMenuClose();
    const confirmed = window.confirm(
      t('playerSession.deletePlayerSessionConfirm') as string,
    );
    if (confirmed) {
      setPlayerIdDeleting(selectedPlayerSession?.player?.id ?? null);
      // if the player session is in a group, delete all sessions in the group
      if (
        (selectedPlayerSession as PlayerSession & {playerSessionIds: number[]})
          ?.playerSessionIds
      ) {
        const res = await Promise.all(
          (
            selectedPlayerSession as PlayerSession & {
              playerSessionIds: number[];
            }
          )?.playerSessionIds?.map(id => deletePlayerSession(id)),
        );
        if (res.some(r => !r?.success)) {
          window.alert(t('playerSession.failedToDeletePlayerSession'));
        }
        // reload the team session group detail
        dispatch(getTeamSessionGroupDetailAsync(teamSessionGroupId as number));
      } else if (selectedPlayerSession?.id) {
        const res = await deletePlayerSession(selectedPlayerSession?.id);
        if (!res?.success) {
          window.alert(t('playerSession.failedToDeletePlayerSession'));
        }
        // reload the team session detail
        dispatch(getTeamSessionDetailAsync(teamSessionId as number));
      }
      setPlayerIdDeleting(null);
    }
    handleMenuClose();
  }, [
    handleMenuClose,
    selectedPlayerSession,
    t,
    dispatch,
    teamSessionGroupId,
    teamSessionId,
  ]);

  const hasNumbers = useMemo(
    () => data.some(playerSession => playerSession.player?.number),
    [data],
  );

  const isGame = sessionType === SessionTypes.game;

  const [sortBy, setSortBy] = useState<string>(
    `${isGame ? 'timeOnIce' : 'activeTime'}-desc`,
  );

  const columns = useMemo(
    () => getColumns({preferredUnits, isGame, t}),
    [preferredUnits, isGame, t],
  );

  const profileWidth = useMemo(() => {
    return hasNumbers ? 250 : 200;
  }, [hasNumbers]);

  const handleSort = useCallback(
    (field: string) => {
      // if the field is already sorted, toggle the direction
      if (sortBy.split('-')[0] === field) {
        setSortBy(
          `${field}-${sortBy.split('-')[1] === 'asc' ? 'desc' : 'asc'}`,
        );
      }
      // otherwise, sort by the field in descending order
      else {
        setSortBy(`${field}-desc`);
      }
    },
    [sortBy],
  );

  const sortedData = useMemo(() => {
    return [...data].sort((a, b) => {
      const [field, direction] = sortBy.split('-');

      if (field === 'position') {
        // sort by position in order by 'center', 'leftwing', 'rightwing', 'defense', 'goalie'
        // then by last name
        const order = [
          Positions.center,
          Positions.leftwing,
          Positions.rightwing,
          Positions.defense,
          Positions.goalie,
        ];

        // First compare positions, if they're the same then compare last names
        const positionCompare =
          direction === 'desc'
            ? order.indexOf(a.player?.profile?.position as Positions) -
              order.indexOf(b.player?.profile?.position as Positions)
            : order.indexOf(b.player?.profile?.position as Positions) -
              order.indexOf(a.player?.profile?.position as Positions);

        if (positionCompare === 0) {
          return (a.player?.profile?.lastName ?? '').localeCompare(
            b.player?.profile?.lastName ?? '',
          );
        }
        return positionCompare;
      }
      // otherwise, sort by the field in descending order
      return direction === 'asc'
        ? ((a[field as keyof PlayerSession] ?? 0) as number) -
            ((b[field as keyof PlayerSession] ?? 0) as number)
        : ((b[field as keyof PlayerSession] ?? 0) as number) -
            ((a[field as keyof PlayerSession] ?? 0) as number);
    });
  }, [data, sortBy]);

  return (
    <Box>
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
        }}>
        <PageSubtitle sx={{mb: 2, mt: 2}}>{t('general.players')}</PageSubtitle>
        <Alert severity="info">{t('teamSessionPlayerList.info')}</Alert>
      </Box>
      <Paper>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            position: 'relative',
          }}>
          <Paper
            sx={{
              width: profileWidth,
              position: 'absolute',
              top: 0,
              left: 0,
              zIndex: 1,
            }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      height: CELL_HEIGHT,
                      '&:hover': {
                        cursor: 'pointer',
                      },
                    }}>
                    <Button
                      size="small"
                      color="info"
                      sx={{display: 'flex', alignItems: 'center'}}
                      onClick={() => handleSort('position')}>
                      {t('teamSessionPlayerList.playerPosition')}
                      {sortBy.split('-')[0] === 'position' &&
                        sortBy.split('-')[1] === 'asc' && (
                          <ArrowUpwardIcon fontSize="small" />
                        )}
                      {sortBy.split('-')[0] === 'position' &&
                        sortBy.split('-')[1] === 'desc' && (
                          <ArrowDownwardIcon fontSize="small" />
                        )}
                      {sortBy.split('-')[0] !== 'position' && (
                        <SwapVertIcon
                          fontSize="small"
                          sx={{
                            opacity: 0.5,
                          }}
                        />
                      )}
                    </Button>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedData.map(playerSession => (
                  <TableRow
                    key={playerSession?.player?.id}
                    sx={{
                      '&:hover': {
                        cursor: 'pointer',
                      },
                    }}
                    onClick={() =>
                      handleNavigateToPlayerSessionDetail(playerSession)
                    }>
                    <TableCell
                      sx={{p: 1, height: CELL_HEIGHT, maxWidth: profileWidth}}>
                      <ProfileIdentity
                        firstName={playerSession.player?.profile?.firstName}
                        lastName={playerSession.player?.profile?.lastName}
                        position={playerSession.player?.profile?.position}
                        number={playerSession.player?.number}
                        expandNumberIfEmpty={hasNumbers}
                        profilePictureUrl={
                          playerSession.player?.profile?.profilePictureFileKey
                        }
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
          <Box
            sx={{
              paddingLeft: `${profileWidth}px`,
              paddingRight: `${OPTIONS_COLUMN_WIDTH}px`,
              width: '100%',
              overflowX: 'scroll',
            }}>
            <Table>
              <TableHead>
                <TableRow>
                  {columns.map(column => (
                    <TableCell
                      key={column.field}
                      sx={{
                        height: CELL_HEIGHT,
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                      }}>
                      <Button
                        size="small"
                        color="info"
                        onClick={() => handleSort(column.field)}>
                        {column.headerName}
                        {sortBy.split('-')[0] === column.field &&
                          sortBy.split('-')[1] === 'asc' && (
                            <ArrowUpwardIcon fontSize="small" />
                          )}
                        {sortBy.split('-')[0] === column.field &&
                          sortBy.split('-')[1] === 'desc' && (
                            <ArrowDownwardIcon fontSize="small" />
                          )}
                        {sortBy.split('-')[0] !== column.field && (
                          <SwapVertIcon
                            fontSize="small"
                            sx={{
                              opacity: 0.5,
                            }}
                          />
                        )}
                      </Button>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedData.map(playerSession => (
                  <TableRow
                    key={playerSession?.player?.id}
                    sx={{
                      '&:hover': {
                        cursor: 'pointer',
                      },
                    }}
                    onClick={() =>
                      handleNavigateToPlayerSessionDetail(playerSession)
                    }>
                    {columns.map(column => (
                      <TableCell
                        key={column.field}
                        sx={{height: CELL_HEIGHT, textAlign: 'center'}}>
                        {column.valueFormatter
                          ? column.valueFormatter(
                              playerSession[column.field] as number,
                            )
                          : (playerSession[column.field] as string)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
          <Paper
            sx={{
              width: OPTIONS_COLUMN_WIDTH,
              position: 'absolute',
              top: 0,
              right: 0,
              zIndex: 1,
            }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{height: CELL_HEIGHT}}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedData.map(playerSession => (
                  <TableRow key={playerSession?.player?.id}>
                    <TableCell
                      sx={{
                        p: 1,
                        height: CELL_HEIGHT,
                        textAlign: 'center',
                      }}>
                      {playerIdDeleting === playerSession?.player?.id ? (
                        <CircularProgress
                          size={20}
                          sx={{
                            color: 'primary.main',
                          }}
                        />
                      ) : (
                        <IconButton
                          size="small"
                          onClick={e => handleMenuOpen(e, playerSession)}>
                          <MoreVertIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
          <Menu
            anchorEl={menuAnchorEl}
            open={Boolean(menuAnchorEl)}
            onClose={handleMenuClose}>
            <MenuItem onClick={handleView}>{t('general.view')}</MenuItem>
            <MenuItem onClick={handleDelete}>{t('general.delete')}</MenuItem>
          </Menu>
        </Box>
      </Paper>
    </Box>
  );
};

export default TeamSessionPlayerList;
